import { supabase } from './supabaseClient'

// ─────────────────────────────
// MIGRATE localStorage TO SUPABASE
// Runs once per user on first login
// ─────────────────────────────

export async function migrateLocalDataToSupabase(userId) {

  const migrated = localStorage.getItem(`tlaw_migrated_${userId}`)
  if (migrated === 'true') return

  console.log('Starting data migration...')

  try {

    // ── MIGRATE WHYS ──
    const whysRaw = localStorage.getItem('tlaw_whys')
    if (whysRaw) {
      const whys = JSON.parse(whysRaw)
      if (whys.length > 0) {
        const whyRows = whys
          .map((text, i) => ({ user_id: userId, why_text: text, position: i }))
          .filter(w => w.why_text)
        if (whyRows.length > 0) {
          await supabase.from('student_whys').upsert(whyRows, { onConflict: 'user_id,position' })
        }
      }
    }

    // ── MIGRATE GOALS ──
    const goalsRaw = localStorage.getItem('tlaw_goals')
    if (goalsRaw) {
      const goals = JSON.parse(goalsRaw)
      if (goals.length > 0) {
        const goalRows = goals
          .map(g => ({
            user_id: userId,
            description: g.description || g.text || g.name,
            target_amount: g.targetAmount || g.target || 0,
            current_amount: g.currentAmount || g.current || g.startingAmount || 0,
            target_date: g.targetDate || g.date || null,
            monthly_needed: g.monthlyNeeded || g.monthly || g.monthlyContribution || null,
            show_on_dashboard: g.showOnDashboard !== false
          }))
          .filter(g => g.description)
        if (goalRows.length > 0) {
          await supabase.from('student_goals').insert(goalRows)
        }
      }
    }

    // ── MIGRATE CHAPTER PROGRESS ──
    const chaptersRaw = localStorage.getItem('tlaw_chapters')
    if (chaptersRaw) {
      const chapters = JSON.parse(chaptersRaw)
      const chapterEntries = Object.entries(chapters)
      if (chapterEntries.length > 0) {
        const progressRows = chapterEntries.map(([chapterId, data]) => ({
          user_id: userId,
          chapter_id: chapterId,
          completed: data.completed || false,
          quiz_score: data.quizScore || data.score || null,
          quiz_passed: data.quizPassed || data.passed || false,
          completed_at: data.completedAt || null
        }))
        await supabase.from('student_progress').upsert(progressRows, { onConflict: 'user_id,chapter_id' })
      }
    } else {
      // Fall back to legacy state shape
      try {
        const rawState = localStorage.getItem('tlw_foundation_state')
        if (rawState) {
          const parsed = JSON.parse(rawState)
          const completed = parsed.completedChapters || []
          const scores = parsed.chapterScores || {}
          if (completed.length > 0) {
            const rows = completed.map(chId => ({
              user_id: userId,
              chapter_id: chId,
              completed: true,
              quiz_score: scores[chId] || null,
              quiz_passed: true
            }))
            await supabase.from('student_progress').upsert(rows, { onConflict: 'user_id,chapter_id' })
          }
        }
      } catch {}
    }

    // ── MIGRATE TRADES ──
    const tradesRaw = localStorage.getItem('tlaw_trades')
    if (tradesRaw) {
      const trades = JSON.parse(tradesRaw)
      if (trades.length > 0) {
        const tradeRows = trades
          .map(t => ({
            user_id: userId,
            symbol: t.symbol || t.ticker,
            strategy: t.strategy,
            entry_date: t.entryDate || t.date || null,
            expiration_date: t.expirationDate || t.expiryDate || t.expiry || null,
            short_strike: t.shortStrike || t.strike || null,
            long_strike: t.longStrike || t.strike2 || null,
            premium: t.premium || t.premiumReceived || t.credit || null,
            contracts: t.contracts || t.qty || 1,
            outcome: t.outcome || t.status || 'Open',
            notes: t.notes || null,
            cost_basis: t.costBasis || null,
            shares_owned: t.sharesOwned || null
          }))
          .filter(t => t.symbol)
        if (tradeRows.length > 0) {
          await supabase.from('student_trades').insert(tradeRows)
        }
      }
    }

    // ── MIGRATE RISK SETTINGS ──
    const baseline = localStorage.getItem('tlaw_risk_baseline')
    if (baseline) {
      await supabase.from('risk_settings').upsert({
        user_id: userId,
        account_type: localStorage.getItem('tlaw_risk_accountType') || 'margin',
        baseline: parseFloat(baseline),
        daily_max: parseFloat(localStorage.getItem('tlaw_risk_dailyMax') || '5'),
        weekly_max: parseFloat(localStorage.getItem('tlaw_risk_weeklyMax') || '10'),
        per_trade: parseFloat(localStorage.getItem('tlaw_risk_perTrade') || '2'),
        personal_floor: parseFloat(localStorage.getItem('tlaw_risk_floor') || '0'),
        dashboard_mode: localStorage.getItem('tlaw_risk_mode') || 'basic'
      }, { onConflict: 'user_id' })
    }

    // ── MIGRATE DAILY LOGS ──
    const logsRaw = localStorage.getItem('tlaw_risk_daily_logs')
    if (logsRaw) {
      const logs = JSON.parse(logsRaw)
      if (logs.length > 0) {
        const logRows = logs
          .map(l => ({
            user_id: userId,
            log_date: l.date,
            opening_balance: l.openingBalance || 0,
            pnl: l.pnl || 0,
            notes: l.notes || null
          }))
          .filter(l => l.log_date)
        if (logRows.length > 0) {
          await supabase.from('daily_logs').upsert(logRows, { onConflict: 'user_id,log_date' })
        }
      }
    }

    // ── MIGRATE STUDENT NAME ──
    const studentName = localStorage.getItem('tlaw_student_name')
    if (studentName) {
      await supabase.from('profiles').update({ first_name: studentName }).eq('id', userId)
    }

    localStorage.setItem(`tlaw_migrated_${userId}`, 'true')
    console.log('Migration complete!')

  } catch (error) {
    console.error('Migration error:', error)
    // Do not mark complete if error — retry next login
  }
}

// ─────────────────────────────
// SAVE DATA TO BOTH STORAGES
// ─────────────────────────────

export async function saveWhy(userId, whys) {
  localStorage.setItem('tlaw_whys', JSON.stringify(whys))
  if (userId) {
    try {
      await supabase.from('student_whys').delete().eq('user_id', userId)
      if (whys.length > 0) {
        await supabase.from('student_whys').insert(
          whys.map((text, i) => ({ user_id: userId, why_text: text, position: i })).filter(w => w.why_text)
        )
      }
    } catch (err) {
      console.error('Save WHY error:', err)
    }
  }
}

export async function saveGoals(userId, goals) {
  localStorage.setItem('tlaw_goals', JSON.stringify(goals))
  if (userId) {
    try {
      await supabase.from('student_goals').delete().eq('user_id', userId)
      if (goals.length > 0) {
        await supabase.from('student_goals').insert(goals.map(g => ({
          user_id: userId,
          description: g.description,
          target_amount: g.targetAmount || 0,
          current_amount: g.currentAmount || 0,
          target_date: g.targetDate || null,
          monthly_needed: g.monthlyNeeded || null,
          show_on_dashboard: g.showOnDashboard !== false
        })))
      }
    } catch (err) {
      console.error('Save goals error:', err)
    }
  }
}

export async function saveProgress(userId, chapterId, progressData) {
  const existing = JSON.parse(localStorage.getItem('tlaw_chapters') || '{}')
  existing[chapterId] = progressData
  localStorage.setItem('tlaw_chapters', JSON.stringify(existing))
  if (userId) {
    try {
      await supabase.from('student_progress').upsert({
        user_id: userId,
        chapter_id: chapterId,
        completed: progressData.completed,
        quiz_score: progressData.quizScore,
        quiz_passed: progressData.quizPassed,
        completed_at: progressData.completedAt,
        updated_at: new Date().toISOString()
      }, { onConflict: 'user_id,chapter_id' })
    } catch (err) {
      console.error('Save progress error:', err)
    }
  }
}

export async function saveTrade(userId, trade) {
  const trades = JSON.parse(localStorage.getItem('tlaw_trades') || '[]')
  const existingIndex = trades.findIndex(t => t.id === trade.id)
  if (existingIndex >= 0) {
    trades[existingIndex] = trade
  } else {
    trades.push(trade)
  }
  localStorage.setItem('tlaw_trades', JSON.stringify(trades))
  if (userId) {
    try {
      await supabase.from('student_trades').upsert({
        id: trade.id,
        user_id: userId,
        symbol: trade.symbol,
        strategy: trade.strategy,
        entry_date: trade.entryDate,
        expiration_date: trade.expirationDate,
        short_strike: trade.shortStrike,
        long_strike: trade.longStrike,
        premium: trade.premium,
        contracts: trade.contracts,
        outcome: trade.outcome,
        notes: trade.notes,
        cost_basis: trade.costBasis,
        shares_owned: trade.sharesOwned
      }, { onConflict: 'id' })
    } catch (err) {
      console.error('Save trade error:', err)
    }
  }
}

export async function saveRiskSettings(userId, settings) {
  Object.entries(settings).forEach(([key, value]) => {
    localStorage.setItem(key, value)
  })
  if (userId) {
    try {
      await supabase.from('risk_settings').upsert({
        user_id: userId,
        account_type: settings.tlaw_risk_accountType,
        baseline: settings.tlaw_risk_baseline,
        daily_max: settings.tlaw_risk_dailyMax,
        weekly_max: settings.tlaw_risk_weeklyMax,
        per_trade: settings.tlaw_risk_perTrade,
        personal_floor: settings.tlaw_risk_floor,
        dashboard_mode: settings.tlaw_risk_mode,
        updated_at: new Date().toISOString()
      }, { onConflict: 'user_id' })
    } catch (err) {
      console.error('Save risk error:', err)
    }
  }
}

export async function saveDailyLog(userId, logEntry) {
  const logs = JSON.parse(localStorage.getItem('tlaw_risk_daily_logs') || '[]')
  const existingIndex = logs.findIndex(l => l.date === logEntry.date)
  if (existingIndex >= 0) {
    logs[existingIndex] = logEntry
  } else {
    logs.push(logEntry)
  }
  localStorage.setItem('tlaw_risk_daily_logs', JSON.stringify(logs))
  if (userId) {
    try {
      await supabase.from('daily_logs').upsert({
        user_id: userId,
        log_date: logEntry.date,
        opening_balance: logEntry.openingBalance,
        pnl: logEntry.pnl || 0,
        notes: logEntry.notes || null
      }, { onConflict: 'user_id,log_date' })
    } catch (err) {
      console.error('Save log error:', err)
    }
  }
}

// ─────────────────────────────
// LOAD DATA FROM SUPABASE
// Restores cloud data to localStorage
// when localStorage is empty (new device/browser)
// ─────────────────────────────

export async function loadDataFromSupabase(userId) {
  if (!userId) return

  try {

    // ── LOAD WHYS ──
    const localWhys = localStorage.getItem('tlaw_whys')
    if (!localWhys || JSON.parse(localWhys).length === 0) {
      const { data: whys } = await supabase
        .from('student_whys').select('*').eq('user_id', userId).order('position')
      if (whys?.length > 0) {
        localStorage.setItem('tlaw_whys', JSON.stringify(whys.map(w => w.why_text)))
      }
    }

    // ── LOAD GOALS ──
    const localGoals = localStorage.getItem('tlaw_goals')
    if (!localGoals || JSON.parse(localGoals).length === 0) {
      const { data: goals } = await supabase
        .from('student_goals').select('*').eq('user_id', userId)
      if (goals?.length > 0) {
        localStorage.setItem('tlaw_goals', JSON.stringify(goals.map(g => ({
          id: g.id,
          description: g.description,
          targetAmount: g.target_amount,
          currentAmount: g.current_amount,
          targetDate: g.target_date,
          monthlyNeeded: g.monthly_needed,
          showOnDashboard: g.show_on_dashboard
        }))))
      }
    }

    // ── LOAD CHAPTER PROGRESS ──
    const localChapters = localStorage.getItem('tlaw_chapters')
    if (!localChapters || Object.keys(JSON.parse(localChapters)).length === 0) {
      const { data: progress } = await supabase
        .from('student_progress').select('*').eq('user_id', userId)
      if (progress?.length > 0) {
        const chaptersObj = {}
        progress.forEach(p => {
          chaptersObj[p.chapter_id] = {
            completed: p.completed,
            quizScore: p.quiz_score,
            quizPassed: p.quiz_passed,
            completedAt: p.completed_at
          }
        })
        localStorage.setItem('tlaw_chapters', JSON.stringify(chaptersObj))
      }
    }

    // ── LOAD TRADES ──
    const localTrades = localStorage.getItem('tlaw_trades')
    if (!localTrades || JSON.parse(localTrades).length === 0) {
      const { data: trades } = await supabase
        .from('student_trades').select('*').eq('user_id', userId)
      if (trades?.length > 0) {
        localStorage.setItem('tlaw_trades', JSON.stringify(trades.map(t => ({
          id: t.id,
          symbol: t.symbol,
          strategy: t.strategy,
          entryDate: t.entry_date,
          expirationDate: t.expiration_date,
          shortStrike: t.short_strike,
          longStrike: t.long_strike,
          premium: t.premium,
          contracts: t.contracts,
          outcome: t.outcome,
          notes: t.notes,
          costBasis: t.cost_basis,
          sharesOwned: t.shares_owned
        }))))
      }
    }

    // ── LOAD RISK SETTINGS ──
    const localBaseline = localStorage.getItem('tlaw_risk_baseline')
    if (!localBaseline) {
      const { data: risk } = await supabase
        .from('risk_settings').select('*').eq('user_id', userId).single()
      if (risk) {
        localStorage.setItem('tlaw_risk_accountType', risk.account_type || 'margin')
        localStorage.setItem('tlaw_risk_baseline', risk.baseline || '')
        localStorage.setItem('tlaw_risk_dailyMax', risk.daily_max || '5')
        localStorage.setItem('tlaw_risk_weeklyMax', risk.weekly_max || '10')
        localStorage.setItem('tlaw_risk_perTrade', risk.per_trade || '2')
        localStorage.setItem('tlaw_risk_floor', risk.personal_floor || '')
        localStorage.setItem('tlaw_risk_mode', risk.dashboard_mode || 'basic')
      }
    }

    // ── LOAD DAILY LOGS ──
    const localLogs = localStorage.getItem('tlaw_risk_daily_logs')
    if (!localLogs || JSON.parse(localLogs).length === 0) {
      const { data: logs } = await supabase
        .from('daily_logs').select('*').eq('user_id', userId)
        .order('log_date', { ascending: false }).limit(30)
      if (logs?.length > 0) {
        localStorage.setItem('tlaw_risk_daily_logs', JSON.stringify(logs.map(l => ({
          date: l.log_date,
          openingBalance: l.opening_balance,
          pnl: l.pnl,
          notes: l.notes,
          timestamp: new Date(l.created_at).getTime()
        }))))
      }
    }

    console.log('Data loaded from Supabase')

  } catch (error) {
    console.error('Load data error:', error)
  }
}
